import type { IdTokenClaims } from "../types/api";
import { Configuration, PopupRequest } from "@azure/msal-browser";

/**
 * Entra ID (Azure AD) Authentication Configuration for Admin Portal
 *
 * SETUP REQUIRED:
 * 1. Create Entra ID App Registration in Azure Portal
 * 2. Add redirect URIs: https://amazon-admin-dee6cce0gfhmcbgp.z01.azurefd.net, http://localhost:5173
 * 3. Create App Roles: "Admin", "SuperAdmin"
 * 4. Assign users to roles in Enterprise Applications
 * 5. Set environment variables in deployment
 */

// Check if we're in production
const isProduction = import.meta.env.PROD;

// Entra ID configuration - use environment variables
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID || "your-client-id-here",
    authority: import.meta.env.VITE_ENTRA_AUTHORITY || "https://login.microsoftonline.com/your-tenant-id-here",
    redirectUri: import.meta.env.VITE_ENTRA_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

// Scopes for login request
export const loginRequest: PopupRequest = {
  scopes: ["User.Read"],
};

// Graph API endpoint for user info
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

/**
 * Check if Entra ID authentication should be used
 * - Production: Always use Entra ID
 * - Development: Use email/OTP (fallback to Entra ID if configured)
 */
export const shouldUseEntraId = (): boolean => {
  if (isProduction) {
    return true; // Production always uses Entra ID
  }

  // In development, use Entra ID only if explicitly configured
  return !!(import.meta.env.VITE_ENTRA_CLIENT_ID && import.meta.env.VITE_ENTRA_AUTHORITY);
};

/**
 * Extract admin role from Entra ID token
 * Roles are defined in the Entra ID App Registration and assigned via Enterprise Applications
 */
export const extractRoleFromToken = (idTokenClaims: Record<string, unknown> | undefined): string => {
  const claims = idTokenClaims as IdTokenClaims | undefined;
  const roles = claims?.roles || [];

  // Check for SuperAdmin role first, then Admin
  if (roles.includes("SuperAdmin")) {
    return "super_admin";
  }
  if (roles.includes("Admin")) {
    return "admin";
  }

  // No admin role found - this shouldn't happen if properly configured
  throw new Error("User does not have required admin role");
};
